import React, { useMemo } from 'react';
import Icon from '@/components/AppIcon';
import EmployeeAvatar from '@/components/employees/EmployeeAvatar';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const statusChip = (status) => {
  switch (status) {
    case 'approved':
      return 'bg-success/20 text-success';
    case 'denied':
      return 'bg-error/20 text-error';
    case 'pending':
    default:
      return 'bg-warning/20 text-warning';
  }
};

const statusLabel = (status, translate) => {
  if (!status) {
    return translate('statuses.pending', 'Pending');
  }
  return translate(`statuses.${status}`, status.charAt(0).toUpperCase() + status.slice(1));
};

const typeIcon = (type) => {
  switch (type) {
    case 'vacation':
      return 'Palmtree';
    case 'sick':
      return 'Heart';
    case 'personal':
      return 'User';
    case 'emergency':
      return 'AlertTriangle';
    default:
      return 'Calendar';
  }
};

const typeLabel = (type, translate) => {
  if (!type) {
    return translate('types.default', 'Absence');
  }
  return translate(`types.${type}`, `${type.charAt(0).toUpperCase()}${type.slice(1)}`);
};

const formatDate = (date, locale) =>
  new Date(date).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

const formatDateRange = (start, end, locale) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  if (startDate.toDateString() === endDate.toDateString()) {
    return formatDate(start, locale);
  }

  const options = { month: 'short', day: 'numeric' };
  return `${startDate.toLocaleDateString(locale, options)} – ${endDate.toLocaleDateString(locale, options)}`;
};

const EmployeeRequestsView = ({ employee, requests = [], onRequestTimeOff }) => {
  const { t, language } = useTranslation(['timeOffManagement', 'common']);
  const translate = (key, fallback) => t(`timeOffManagement:${key}`, fallback);
  const locale = language || 'en-US';
  const sortedRequests = useMemo(
    () =>
      [...requests].sort(
        (a, b) => new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime()
      ),
    [requests]
  );

  const { counts, nextRequest, upcomingCount } = useMemo(() => {
    const tally = { total: requests.length, approved: 0, pending: 0, denied: 0 };
    const upcoming = [];
    const now = new Date();

    requests.forEach((request) => {
      tally[request.status] = (tally[request.status] || 0) + 1;
      const start = new Date(request.startDate);
      if (start >= now) {
        upcoming.push(request);
      }
    });

    upcoming.sort(
      (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    );

    return {
      counts: tally,
      nextRequest: upcoming[0] || null,
      upcomingCount: upcoming.length
    };
  }, [requests]);

  const displayName = employee?.name ?? translate('employeeView.profile.nameFallback', 'Your Requests');
  const displayPosition = employee?.position ?? translate('defaults.positionFallback', 'Team Member');
  const department = employee?.department
    ? employee.department.charAt(0).toUpperCase() + employee.department.slice(1)
    : translate('employeeView.profile.departmentFallback', 'Restaurant Team');
  const avatarUrl = employee?.avatarUrl ?? null;
  const avatarAlt = formatTranslation(
    translate('card.avatarAlt', '{{name}} profile photo'),
    { name: displayName },
  );
  const avatarStatus = employee?.status ?? null;

  return (
    <div className="space-y-6">
      <section className="bg-card border border-border rounded-lg shadow-elevation-1 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="flex items-start gap-4">
          <EmployeeAvatar
            url={avatarUrl}
            name={displayName}
            alt={avatarAlt}
            status={avatarStatus}
            size="lg"
            showStatus={Boolean(avatarStatus)}
            wrapperClassName="border border-border"
            />
            <div>
              <h2 className="text-xl font-semibold text-foreground">{displayName}</h2>
              <p className="text-sm text-muted-foreground">
                {displayPosition} • {department}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="bg-muted/30 rounded-md p-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {translate('statuses.approved', 'Approved')}
                  </p>
                  <p className="text-lg font-semibold text-foreground">{counts.approved || 0}</p>
                </div>
                <div className="bg-muted/30 rounded-md p-3">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {translate('statuses.pending', 'Pending')}
                  </p>
                  <p className="text-lg font-semibold text-foreground">{counts.pending || 0}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-4 flex-1 lg:max-w-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {translate('employeeView.upcoming.title', 'Upcoming Requests')}
              </span>
              <span className="text-sm text-muted-foreground">
                {formatTranslation(
                  translate(
                    `employeeView.upcoming.count.${upcomingCount === 1 ? 'singular' : 'plural'}`,
                    upcomingCount === 1 ? '{{count}} request' : '{{count}} requests',
                  ),
                  { count: upcomingCount },
                )}
              </span>
            </div>
            {nextRequest ? (
              <div>
                <p className="text-sm font-semibold text-foreground">
                  {formatDateRange(nextRequest.startDate, nextRequest.endDate, locale)}
                </p>
                <p className="text-xs text-muted-foreground mt-1 capitalize">
                  {typeLabel(nextRequest.type, translate)} • {formatTranslation(
                    translate(
                      `duration.days.${nextRequest.duration === 1 ? 'singular' : 'plural'}`,
                      nextRequest.duration === 1 ? '{{count}} day' : '{{count}} days',
                    ),
                    { count: nextRequest.duration ?? 0 },
                  )}
                </p>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-3 ${statusChip(
                    nextRequest.status
                  )}`}
                >
                  {statusLabel(nextRequest.status, translate)}
                </span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {translate('employeeView.upcoming.empty', 'No upcoming absences currently scheduled.')}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
        <div className="flex flex-col gap-3 px-6 py-4 border-b border-border md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {translate('employeeView.requests.title', 'My Absence Requests')}
            </h3>
            <div className="text-sm text-muted-foreground">
              {formatTranslation(
                translate('employeeView.requests.summary', '{{total}} total • {{pending}} pending'),
                { total: counts.total ?? 0, pending: counts.pending || 0 },
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {sortedRequests.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-1">
                {translate('employeeView.requests.emptyTitle', 'No requests yet')}
              </h4>
              <p className="text-sm text-muted-foreground">
                {translate(
                  'employeeView.requests.emptyDescription',
                  'Your submitted absence requests will appear here once you create them.',
                )}
              </p>
            </div>
          ) : (
            sortedRequests.map((request) => (
              <div
                key={request.id}
                className="border border-border rounded-lg p-4 hover:shadow-elevation-2 transition-smooth"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-muted/40 rounded-md">
                      <Icon name={typeIcon(request.type)} size={18} className="text-muted-foreground" />
                    </div>
                    <div>
                      <h4 className="text-base font-semibold text-foreground capitalize">
                        {formatTranslation(
                          translate('employeeView.requests.cardTitle', '{{type}} request'),
                          { type: typeLabel(request.type, translate) },
                        )}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {formatTranslation(
                          translate('employeeView.requests.submitted', 'Submitted {{date}} • {{duration}}'),
                          {
                            date: formatDate(request.submittedDate, locale),
                            duration: formatTranslation(
                              translate(
                                `duration.days.${request.duration === 1 ? 'singular' : 'plural'}`,
                                request.duration === 1 ? '{{count}} day' : '{{count}} days',
                              ),
                              { count: request.duration ?? 0 },
                            ),
                          },
                        )}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-medium ${statusChip(
                      request.status
                    )}`}
                  >
                    {statusLabel(request.status, translate)}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Calendar" size={16} />
                    <span className="text-foreground">
                      {formatDateRange(request.startDate, request.endDate, locale)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Clock" size={16} />
                    <span className="text-foreground capitalize">
                      {typeLabel(request.type, translate)} •{' '}
                      {formatTranslation(
                        translate(
                          `duration.days.${request.duration === 1 ? 'singular' : 'plural'}`,
                          request.duration === 1 ? '{{count}} day' : '{{count}} days',
                        ),
                        { count: request.duration ?? 0 },
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="Users" size={16} />
                    <span className="text-foreground">
                      {formatTranslation(
                        translate('employeeView.requests.coverage', 'Requested coverage: {{level}}'),
                        { level: request.coverageImpact?.level || 'n/a' },
                      )}
                    </span>
                  </div>
                </div>

                {request.reason && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                      {translate('employeeView.requests.reasonLabel', 'Reason')}
                    </p>
                    <p className="text-sm text-foreground bg-muted/30 rounded-md p-3">
                      {request.reason}
                    </p>
                  </div>
                )}

                {request.managerResponse && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                      {translate('employeeView.requests.managerResponse', 'Manager Response')}
                    </p>
                    <div className="flex items-start gap-2 text-sm">
                      <Icon name="MessageSquare" size={16} className="mt-0.5 text-muted-foreground" />
                      <p className="text-foreground">{request.managerResponse}</p>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default EmployeeRequestsView;
